/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Stack,
    TextField,
    IconButton,
    CircularProgress,
    Chip,
    Button,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch } from 'src/redux/store';
import { parseApiDate } from 'src/utils/taskDateUtils';
import {
    addChecklistItem,
    completeTask,
} from 'src/redux/slices/taskSlice';
import ChecklistItemRow from './ChecklistItemRow';
import ChecklistItemPreviewRow from './ChecklistItemPreviewRow';

// -----------------------------------------------

function formatDateTime(dateStr) {
    if (!dateStr) return '';
    const d = parseApiDate(dateStr)?.toDate() || new Date();
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// -----------------------------------------------

export default function TaskDetailChecklist({
    items,
    practiceId,
    taskId,
    leadData,
    onItemToggle,
    mode,
    isCompleted,
    onComplete,
}) {
    const [addingItem, setAddingItem] = useState(false);
    const [newItemText, setNewItemText] = useState('');
    const [completing, setCompleting] = useState(false);

    const isPreview = mode === 'preview';

    // PR6 of v4 plan -- preview mode renders a read-only projection
    // from the BE's checklist_specs payload (PR2 transform). No live
    // ORM rows, no completion controls, no add-row footer.
    if (isPreview) {
        const total = items.length;
        const skipped = items.filter((s) => s && s.skip).length;
        return (
            <Box data-testid="task-detail-checklist-preview">
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {total} item{total === 1 ? '' : 's'}
                        {skipped > 0 && (
                            <> · <strong>{skipped}</strong> skipped</>
                        )}
                    </Typography>
                    <Chip
                        label="Preview"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem', ml: 'auto' }}
                    />
                </Stack>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                    }}
                >
                    {total === 0 ? (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.disabled',
                                fontStyle: 'italic',
                                p: 1.5,
                            }}
                        >
                            No checklist items projected.
                        </Typography>
                    ) : (
                        items.map((spec, idx) => (
                            <ChecklistItemPreviewRow
                                key={spec.template_item_id || `idx-${idx}`}
                                spec={spec}
                            />
                        ))
                    )}
                </Box>
            </Box>
        );
    }

    const completed = items.filter((item) => item.is_completed || item.completed || item.completion_state === 'YES');
    const total = items.length;
    const progress = total > 0 ? (completed.length / total) * 100 : 0;

    const handleCompleteTask = () => {
        if (!practiceId || !taskId) return;
        setCompleting(true);
        dispatch(
            completeTask(practiceId, taskId, ({ success }) => {
                setCompleting(false);
                if (success && onComplete) onComplete();
            })
        );
    };

    const handleAddItem = () => {
        const text = newItemText.trim();
        if (!text) return;

        setAddingItem(true);
        dispatch(
            addChecklistItem(practiceId, taskId, { title: text }, ({ success }) => {
                if (success) {
                    setNewItemText('');
                    if (onItemToggle) onItemToggle();
                }
                setAddingItem(false);
            })
        );
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddItem();
        }
    };

    return (
        <Box>
            {/* Progress bar */}
            {total > 0 && (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(0,0,0,0.08)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: progress === 100 ? '#00AB55' : '#078DEE',
                            },
                        }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {completed.length} / {total}
                    </Typography>
                </Stack>
            )}

            {/* Checklist items */}
            <Box sx={{ mb: 1.5 }}>
                {items.map((item) => {
                    const itemCompleted = item.is_completed || item.completed || item.completion_state === 'YES';
                    const completedBy = item.completed_by;
                    const completedByName = completedBy
                        ? `${completedBy.first_name || ''} ${completedBy.last_name || ''}`.trim() || completedBy.email || 'Someone'
                        : null;
                    const completedAt = item.completed_at || item.updated_at;

                    return (
                        <Box key={item.id} sx={{ mb: 0.25 }}>
                            <ChecklistItemRow
                                item={item}
                                practiceId={practiceId}
                                taskId={taskId}
                                leadData={leadData}
                                onDone={onItemToggle}
                            />
                            {/* Audit trail */}
                            {itemCompleted && completedByName && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.disabled', display: 'block', pl: 5, mt: '-2px', mb: 0.5, fontSize: '0.72rem' }}
                                >
                                    Completed by {completedByName}
                                    {completedAt && <> · {formatDateTime(completedAt)}</>}
                                </Typography>
                            )}
                        </Box>
                    );
                })}

                {total === 0 && (
                    <Box sx={{ py: 1 }}>
                        {isCompleted ? (
                            <Chip
                                icon={<Iconify icon="eva:checkmark-circle-2-fill" width={16} />}
                                label="Task completed"
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(0,171,85,0.12)',
                                    color: '#007B55',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    height: 28,
                                    '& .MuiChip-icon': { color: '#00AB55', ml: 0.5 },
                                }}
                            />
                        ) : (
                            <Button
                                variant="contained"
                                size="small"
                                disabled={completing || !practiceId || !taskId}
                                onClick={handleCompleteTask}
                                startIcon={completing
                                    ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                    : <Iconify icon="eva:checkmark-circle-2-outline" width={16} />
                                }
                                sx={{
                                    bgcolor: '#00AB55',
                                    '&:hover': { bgcolor: '#007B55' },
                                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    borderRadius: 1.5,
                                    px: 2,
                                }}
                            >
                                {completing ? 'Completing…' : 'Mark task complete'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {/* Add item row */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Add a checklist item…"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={addingItem}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            fontSize: '0.875rem',
                        },
                    }}
                />
                <IconButton
                    size="small"
                    onClick={handleAddItem}
                    disabled={!newItemText.trim() || addingItem}
                    sx={{
                        bgcolor: newItemText.trim() ? '#00AB55' : 'action.disabledBackground',
                        color: newItemText.trim() ? '#fff' : 'text.disabled',
                        '&:hover': { bgcolor: '#007B55' },
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                    }}
                >
                    {addingItem ? (
                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                    ) : (
                        <Iconify icon="eva:plus-fill" width={18} />
                    )}
                </IconButton>
            </Stack>
        </Box>
    );
}

TaskDetailChecklist.propTypes = {
    items: PropTypes.array,
    // practiceId / taskId are only required for ``mode='live'``
    // (default). In ``mode='preview'`` the component renders a
    // read-only projection from the BE's checklist_specs payload
    // and never dispatches addChecklistItem, so they're optional.
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.string,
    leadData: PropTypes.object,
    onItemToggle: PropTypes.func,
    // PR6 of v4 plan -- 'preview' renders a read-only projection of
    // the projected V2 checklist (BE checklist_specs shape) without
    // any ChecklistItem.id, completion state, or completed_by data.
    mode: PropTypes.oneOf(['live', 'preview']),
    // Whether the parent task is already completed. When total===0 and
    // isCompleted is true the component renders a "completed" chip instead
    // of the "Mark task complete" button.
    isCompleted: PropTypes.bool,
    // Called after completeTask succeeds — parent should refresh task state.
    onComplete: PropTypes.func,
};

TaskDetailChecklist.defaultProps = {
    items: [],
    practiceId: null,
    taskId: '',
    leadData: null,
    onItemToggle: null,
    mode: 'live',
    isCompleted: false,
    onComplete: null,
};
